import { useMemo, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import { useClient, useClients } from '../../hooks/useClients';
import { usePeople } from '../../hooks/usePeople';
import { useCreatePost } from '../../hooks/useSocialPosts';
import { clientLabel } from '../../lib/clientLabel';
import { parseEstimate } from '../../lib/estimate';
import { setPostApproval } from '../../lib/socialPostsApi';
import {
  FORMAT_LABELS,
  PLATFORM_LABELS,
  SOCIAL_FORMATS,
  SOCIAL_PLATFORMS,
  type SocialPlatform,
  type SocialPostFormat,
} from '../../lib/socialPostsApi';
import { DateField } from '../ui/DateField';
import { TimeField } from '../ui/TimeField';
import { Select } from '../ui/Select';

interface PostFormModalProps {
  /** Pre-selected client (the client page's calendar tab locks it). */
  clientId?: string;
  lockClient?: boolean;
  /** Pre-filled publishing day, YYYY-MM-DD (clicking a day in the calendar). */
  date?: string;
  onClose: () => void;
  onCreated?: (taskId: string) => void;
}

/**
 * „Postare nouă”: a task with its publishing side, created in one go. The
 * fields mirror a task (brief, assignee, production deadline, estimate) plus
 * what gets published (platform, format, date and time, caption, media link).
 * One platform per post — the same reel on Instagram and TikTok is two posts
 * (the post page has „Duplică pe altă platformă”).
 */
export function PostFormModal({
  clientId: initialClientId,
  lockClient = false,
  date,
  onClose,
  onCreated,
}: PostFormModalProps) {
  const create = useCreatePost();
  const { data: clientsData } = useClients({ limit: 100, active: 'true', sort: 'name' });
  const { data: people } = usePeople({ active: 'true' });

  const [clientId, setClientId] = useState(initialClientId ?? '');
  const { data: client } = useClient(clientId || undefined);
  const [projectId, setProjectId] = useState('');
  const [title, setTitle] = useState('');
  const [platform, setPlatform] = useState<SocialPlatform>('INSTAGRAM');
  const [format, setFormat] = useState<SocialPostFormat>('POST');
  const [publishDate, setPublishDate] = useState(date ?? '');
  const [publishTime, setPublishTime] = useState('12:00');
  const [caption, setCaption] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [estimate, setEstimate] = useState('');
  const [description, setDescription] = useState('');
  const [sendForApproval, setSendForApproval] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clientOptions = useMemo(
    () => (clientsData?.data ?? []).map((c) => ({ value: c.id, label: clientLabel(c) })),
    [clientsData],
  );
  const projectOptions = useMemo(
    () =>
      (client?.projects ?? [])
        .filter((p) => !['COMPLETED', 'CANCELLED'].includes(p.status))
        .map((p) => ({ value: p.id, label: p.code ? `${p.code} · ${p.name}` : p.name })),
    [client],
  );
  const assigneeOptions = useMemo(
    () =>
      (people ?? [])
        .filter((p) => p.userId)
        .map((p) => ({ value: p.userId as string, label: p.fullName })),
    [people],
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!clientId) return setError('Alege clientul.');
    if (!title.trim()) return setError('Titlul este obligatoriu.');
    if (!publishDate) return setError('Alege ziua publicării.');
    const parsed = parseEstimate(estimate);
    if (parsed.error) return setError('Estimarea trebuie să fie o durată (ex: 1h 30m).');
    const [y, m, d] = publishDate.split('-').map(Number);
    const [hh, mm] = (publishTime || '12:00').split(':').map(Number);
    const publishAt = new Date(y, m - 1, d, hh, mm).toISOString();
    try {
      const post = await create.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        clientId,
        projectId: projectId || null,
        assigneeId: assigneeId || null,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        estimate: parsed.hours,
        platform,
        format,
        publishAt,
        caption: caption.trim() || null,
        mediaUrl: mediaUrl.trim() || null,
      });
      if (sendForApproval) await setPostApproval(post.id, 'PENDING_CLIENT');
      onCreated?.(post.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Postarea nu a putut fi salvată.');
    }
  }

  const validUrl = /^https?:\/\/\S+$/i.test(mediaUrl.trim());

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-card shadow-xl">
        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h2 className="text-lg font-semibold text-gray-900">Postare nouă</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Închide"
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:grid-cols-2">
            <Select
              label="Client"
              searchable
              value={clientId}
              onChange={(v) => {
                setClientId(v);
                setProjectId('');
              }}
              disabled={lockClient}
              placeholder="Alege clientul"
              options={clientOptions}
            />
            <Select
              label="Proiect (opțional)"
              clearable
              value={projectId}
              onChange={setProjectId}
              placeholder={clientId ? 'Pe client, fără proiect' : 'Alege întâi clientul'}
              disabled={!clientId || projectOptions.length === 0}
              options={projectOptions}
            />

            <label className="flex flex-col text-sm font-medium text-gray-700 sm:col-span-2">
              Titlu
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="ex. Reel lansare colecție de toamnă"
                className="ui-input mt-1"
                required
              />
            </label>

            <Select
              label="Platformă"
              value={platform}
              onChange={(v) => setPlatform(v as SocialPlatform)}
              options={SOCIAL_PLATFORMS.map((p) => ({ value: p, label: PLATFORM_LABELS[p] }))}
            />
            <Select
              label="Format"
              value={format}
              onChange={(v) => setFormat(v as SocialPostFormat)}
              options={SOCIAL_FORMATS.map((f) => ({ value: f, label: FORMAT_LABELS[f] }))}
            />

            <label className="flex flex-col text-sm font-medium text-gray-700">
              Ziua publicării
              <DateField
                value={publishDate}
                onChange={setPublishDate}
                required
                className="mt-1 w-full"
              />
            </label>
            <label className="flex flex-col text-sm font-medium text-gray-700">
              Ora
              <TimeField
                value={publishTime}
                onChange={setPublishTime}
                step={15}
                required
                className="mt-1 w-full"
              />
            </label>

            <label className="flex flex-col text-sm font-medium text-gray-700 sm:col-span-2">
              Textul postării
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={4}
                placeholder="Ce apare sub postare, cu hashtag-uri"
                className="ui-textarea mt-1"
              />
            </label>

            <label className="flex flex-col text-sm font-medium text-gray-700 sm:col-span-2">
              Link video / material (Digi Storage, Drive…)
              <div className="mt-1 flex gap-2">
                <input
                  type="url"
                  value={mediaUrl}
                  onChange={(e) => setMediaUrl(e.target.value)}
                  placeholder="https://…"
                  className="ui-input w-full"
                />
                {validUrl && (
                  <a
                    href={mediaUrl.trim()}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <ExternalLink size={14} /> Deschide
                  </a>
                )}
              </div>
            </label>

            <Select
              label="Responsabil"
              searchable
              clearable
              value={assigneeId}
              onChange={setAssigneeId}
              placeholder="—"
              options={assigneeOptions}
            />
            <label className="flex flex-col text-sm font-medium text-gray-700">
              Termen de producție
              <DateField value={dueDate} onChange={setDueDate} className="mt-1 w-full" />
            </label>
            <label className="flex flex-col text-sm font-medium text-gray-700">
              Estimare
              <input
                value={estimate}
                onChange={(e) => setEstimate(e.target.value)}
                placeholder="ex: 2h"
                className="ui-input mt-1"
              />
            </label>

            <label className="flex flex-col text-sm font-medium text-gray-700 sm:col-span-2">
              Brief intern (nu îl vede clientul)
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="ui-textarea mt-1"
              />
            </label>

            <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
              <input
                type="checkbox"
                checked={sendForApproval}
                onChange={(e) => setSendForApproval(e.target.checked)}
              />
              Trimite direct spre aprobare la client
            </label>
          </div>

          {error && <p className="px-5 text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
            <button type="button" onClick={onClose} className="ui-btn-secondary">
              Anulează
            </button>
            <button type="submit" disabled={create.isPending} className="ui-btn-primary">
              {create.isPending ? 'Se salvează…' : 'Creează postarea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
